import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import logo from "../../assets/images/logo.png";
import { FormFeedback, Input, Label, Spinner, Alert } from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import Banner from "./AuthBanner";
import { useFormik } from "formik";
import { resetPassword } from "../../slices/auth/resetPassword/thunk";
import { resetPasswordError } from "../../slices/auth/resetPassword/reducer";

const resetPasswordSchema = Yup.object().shape({
  new_password: Yup.string()
    .required("New Password is required *")
    .min(8, "Password must be at least 8 characters *")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter *")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter *")
    .matches(/\d/, "Password must contain at least one number *")
    .matches(
      /[@$!%*?&#^()[\]{}\-_=+<>]/,
      "Password must contain at least one special character *"
    ),
  new_confirm_password: Yup.string()
    .required("Confirm Password is required *")
    .oneOf([Yup.ref("new_password")], "Passwords must match *"),
});


const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [alertMessage, setAlertMessage] = React.useState(null);
  const [alertType, setAlertType] = React.useState(null);
  const [showAlert, setShowAlert] = React.useState(false);


  const loading = useSelector((state) => state.ResetPassword.loading);

  const resetPasswordError = useSelector(
    (state) => state.ResetPassword.resetPasswordError
  );

  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get("email") || "";
  const otpCode = queryParams.get("otp") || "";

  const formik = useFormik({
    initialValues: {
      email: email,
      new_password: "",
      new_confirm_password: "",
      password_reset_otp: parseInt(otpCode, 10),
    },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values) => {
      try {
        const data = {
          email: values.email,
          password: values.new_password,
        };

        const res = await dispatch(resetPassword(data));

        if (res?.message) {
          setAlertMessage(res?.message);
          setAlertType("success");
          setShowAlert(true);

          setTimeout(() => {
            setShowAlert(false);
            navigate("/login");
          }, 3000);
        } else {
          setAlertMessage(resetPasswordError || "Something went wrong. Please try again.");
          setAlertType("danger");
          setShowAlert(true);

          setTimeout(() => {
            setShowAlert(false);
          }, 4000);
        }

      } catch (error) {
        console.log("error", error);

      }
    },
  });

  document.title = "Set Password | D&A";

  return (
    <React.Fragment>
      <div className="loginPage">
        <Banner />
        <div className="loginWrapper">
          <div className="loginContent">
            {showAlert && (
              <div className="mb-3">
                <Alert
                  color={alertType}
                  toggle={() => setShowAlert(false)}
                  fade={true}
                >
                  {alertMessage}
                </Alert>
              </div>
            )}

            <div className="headerText">
              <img
                loading="lazy"
                className="logoImage"
                src={logo}
                alt="Company logo"
              />
              <h1 className="welcomeBack">Reset Password</h1>
              <p className="loginInstructions">
                Your new password must be different from previous used password.
              </p>
            </div>

            <form className="loginForm" onSubmit={formik.handleSubmit}>
              <div className="inputField">
                <Label htmlFor="new_password" className="inputLabel">
                  New Password
                </Label>
                <Input
                  id="new_password"
                  name="new_password"
                  className="form-control textInput"
                  type="password"
                  placeholder="Enter New Password"
                  value={formik.values.new_password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.new_password &&
                    !!formik.errors.new_password
                  }
                />
                {formik.touched.new_password && formik.errors.new_password && (
                  <FormFeedback>{formik.errors.new_password}</FormFeedback>
                )}
              </div>

              <div className="inputField">
                <Label htmlFor="new_confirm_password" className="inputLabel">
                  Confirm New Password
                </Label>
                <Input
                  id="new_confirm_password"
                  name="new_confirm_password"
                  className="form-control textInput"
                  type="password"
                  placeholder="Re-enter New Password"
                  value={formik.values.new_confirm_password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    formik.touched.new_confirm_password &&
                    !!formik.errors.new_confirm_password
                  }
                />
                {formik.touched.new_confirm_password &&
                  formik.errors.new_confirm_password && (
                    <FormFeedback>
                      {formik.errors.new_confirm_password}
                    </FormFeedback>
                  )}
              </div>

              <button type="submit" className="signInButton">
                {loading ? <Spinner /> : "Reset Password"}
              </button>

              <div className="d-flex align-items-center mx-auto mt-4 gap-1">
                <p
                  className="signUpPrompt cursor-pointer"
                  onClick={() => {
                    navigate("/login");
                  }}
                >
                  Back to Login{" "}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ResetPassword;
